//constants
const COOKIE_POLICY_NAME = "eg_cookieconsent_status";

/* Utils */

const logger = {
  info: (message) => {
    window.Sentry && window.Sentry.captureMessage(message);
  },
  error: (err) => {
    window.Sentry && window.Sentry.captureException(err);
  },
};

function getCapiData() {
  const data = {
    fbp: null,
    fbc: null,
    userAgent: null,
  };

  try {
    data.fbp = getCookie("_fbp") || null;
    data.fbc = getCookie("_fbc") || null;
    data.userAgent = window.navigator.userAgent || null;
  } catch (e) {
    logger.error(e);
  }

  return data;
}

/**
 * Asynchronously load a script
 * @param {string} src
 * @param {string} type
 * @returns Promise
 */
window.loadedScripts = {}
const loadScript = (src, type = "text/javascript") => {
  const head = document.querySelector("head");
  if (!window.loadedScripts[src]) {
    window.loadedScripts[src] = new Promise((r) => {
      let l = false;
      const script = document.createElement("script");
      script.type = type;
      script.src = src;
      script.async = true;
      script.onload = script.onreadystatechange = function () {
        if (!l && (!this.readyState || this.readyState === 'complete')) {
          l = true
          r()
        }
      }
        head.appendChild(script);
    });
  }
  return window.loadedScripts[src]
};

/**
 * Returns the path after adding the new query param to it
 * @param path — The current path
 * @param key — The query param key
 * @param value — The query param value
 */
function addQueryParam(path = "", key = "", value = "") {
  let finalPath = path;
  if (finalPath.indexOf("?") !== -1) {
    finalPath = finalPath + `&${key}=${value}`;
  } else {
    finalPath = finalPath + `?${key}=${value}`;
  }
  return finalPath;
}

function isValidEmail(email) {
  const regEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regEx.test(String(email).toLowerCase());
}

function parseSearchParams(search) {
  if (!search) {
    return {};
  }

  return search
    .replace(/^\?/, "")
    .split("&")
    .map((param) => param.split("="))
    .reduce(
      (acc, [k, v]) => ({
        ...acc,
        [window.decodeURIComponent(k)]: window.decodeURIComponent(v),
      }),
      {}
    );
}

function isLoggedIn() {
  return !!getCookie("userToken");
}

const apiFetch = (BASE_URL, path, opts) => {
  return fetch(`${BASE_URL}${path}`, opts);
};

const apiFetchJson = async (BASE_URL, path, opts) => {
  const req = await fetch(`${BASE_URL}${path}`, opts);
  return req.json();
};

const apiFetchPmallStoreFront = async (path, method = "GET", body = {}) => {
  return apiFetchJson(theme.settings.pmall_storefront_api_url, path, {
    headers: {
      "pangaea-brand-public-key": theme.settings.pmall_brand_api_key,
      "Content-Type": "application/json",
    },
    method,
    body: JSON.stringify(body),
  });
};

const apiFetchBrandServices = async (path, opts = {}) => {
  const headers = Object.assign({}, opts.headers, {
    tenant: theme.settings.pmall_brand_api_key,
  });

  const finalOpts = Object.assign({}, opts, {
    headers: headers,
  });
  return apiFetchJson(
    theme.settings.pmall_brand_services_api_url,
    addQueryParam(path, "brand", theme.settings.pmall_brand_api_key),
    finalOpts
  );
};

const getAvailablePaymentMethods = async (countryCode = "", currency = "") => {
  try {
    const cacheKey = `${currency.toLowerCase()}_${countryCode.toLowerCase()}`;
    const paymentMethods = getCookie("paymentMethods");
    const parsedPaymentMethods = JSON.parse(paymentMethods || "{}");
    const savedPaymentMethod = parsedPaymentMethods[cacheKey];
    if (savedPaymentMethod) {
      return savedPaymentMethod;
    }

    const res = await apiFetchBrandServices(
      `/api/processor-map/providers?countryCode=${countryCode}&currency=${currency}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    setCookie(
      "paymentMethods",
      JSON.stringify({ [cacheKey]: res.orderedMethods })
    );
    return res.orderedMethods;
  } catch (error) {
    logger.error(error);
    return {};
  }
};



/*
DATYPES

interface EcommerceProduct {
  id: string | number
  name: string
  brand?: string
  category?: string
  variant?: string
  // In the client's selected currency
  price?: number
}

interface EcommerceImpression extends EcommerceProduct {
  // A consistent identifier of the list where this impression occurred
  list?: string
  // The position of this product in the list
  position?: number
}

interface EcommerceCartAction extends EcommerceProduct {
  // The number of products that were added or removed from the cart.
  // **not the total**
  quantity: number
  currency?: string
}
interface PageView {
  path: string
  url?: string
  title?: string
  type?: ANALYTICS_PAGE_TYPES
  utmParams?: Record<string, string>
  queryParams?: Record<string, string>
  freeTrialAttributes?: FreeTrialAttributes
}

type ANALYTICS_PAGE_TYPES =
  | 'error'
  | 'checkout'
  | 'offer_page'
  | 'landing_page'
  | 'home_page'
  | 'product_page'
  | 'all_or_multiple_products'
  | 'blog'
  | 'how-to'
  | 'about'
  | 'account-system'

*/

/**
 * This function should not be used directly. Prefer one of the more specific
 * functions below.
 *
 * @param data {[key: string] :  any } - The data to push into the datalayer. e.g
 * @returns {Promoise<void>}
 */
const layerPush = (data) => {
  return new Promise((resolve) => {
    data.codeSource = theme.settings.analytics_code_source;

    const currentCallback = data.eventCallback;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign({}, data, {
        eventCallback: () => {
          if (typeof currentCallback === "function") {
            currentCallback();
          }
          resolve();
        },
      })
    );

    if (!window.google_tag_manager) {
      if (typeof currentCallback === "function") {
        currentCallback();
      }
      resolve();
    }
    console.log("Pushed", data);
  });
};

/**
 * This event is triggered each time we visit a page.
 * @param {PageView} pageData Refer to the PageView defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackPageView = (pageData) => {
  return layerPush({
    event: "pageView",
    page: pageData,
  });
};

/**
 * Push an event or user data to Optimizely
 * @param {object} data
 */
const optimizelyPush = (data) => {
  window["optimizely"] = window["optimizely"] || [];
  window["optimizely"].push(data);
};

const trackExperimentActivated = (experimentName) => {
  return layerPush({
    event: 'experiment_activated',
    experiment_api_name: experimentName,
  })
};

const activateOptimizelyPage = async (pageName) => {
  return new Promise((resolve) => {
    optimizelyPush({
      type: 'page',
      pageName: pageName,
    })

    trackExperimentActivated(pageName)
    // set timeout is used to give the optimizely page activation time to "activate" and call the variation code
    setTimeout(() => {
      resolve()
    }, 0)
  })
};

/**
 * This event is triggered each time we visit a page.
 * @param {EcommerceImpression} impressions Refer to the EcommerceImpression defination in the DATATYPES above
 * @param {currencyCode} string  The cart currency
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductImpression = (impressions, currencyCode) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceImpression",
    ecommerce: { impressions, currencyCode },
  });
};

/**
 * This event is triggered each time we click on a product link that takes us to a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductClick = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceProductClick",
    ecommerce: {
      click: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time we visit a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductDetail = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceDetailPage",
    ecommerce: {
      detail: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time we add an item to the cart
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackCartAdd = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceCartAdd",
    ecommerce: {
      add: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time we remove an item from the cart
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackCartRemove = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceCartRemove",
    ecommerce: {
      remove: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time the user state is updated
 * @param {Object} traits
 */
const trackUserTraits = (traits) => {
  layerPush({
    event: "identifiedUserTraits",
    traits,
  });
};

/**
 * This event is triggered when the currency modal automatically opens or when the user manually changes the currency
 * or language
 */
 const trackCurrencyModal = (storeCountry, storeLanguage) => {
  return layerPush({
    event: 'storeSwitched',
    storeCountry: storeCountry,
    storeLanguage: storeLanguage,
  });
};


// Optimizely Feature Flag
function featureFlag(flagKey, defaultValue = false) {
  const val = EXPERIMENT_FLAGS[flagKey];
  if (val === undefined) {
    return defaultValue
  }
  return val
}

function useFeatureFlag(flag, defaultValue, onChange = () => {/* fix SonarQube warning */}) {
    const currentValue = featureFlag(flag, defaultValue);
    if (flag) {
      if (currentValue != defaultValue) {
        onChange(currentValue);
      }
      window.removeEventListener(flag, onChange);
      window.addEventListener(flag, () => {
        onChange(featureFlag(flag, defaultValue));
      });
    }
}

const EXPERIMENT_FLAGS = {};

if (window.optimizelyFeatureFlags && Array.isArray(window.optimizelyFeatureFlags)) {
  window.optimizelyFeatureFlags.forEach((entry) => {
    EXPERIMENT_FLAGS[entry.key] = entry.value;
    })
  }
  window.optimizelyFeatureFlags = {
    push(entry = {}) {
      EXPERIMENT_FLAGS[entry.key] = entry.value;
      window.dispatchEvent(new Event(entry.key))
    },
  }